<template>
    <PageLayoutForms>
        <template #header>
            <h1 class="pdp-mandate-onboarding-company__title typographies-heading-h1">
                {{ $t.__('pdp.mandate.Onboarding.title') }}
            </h1>
        </template>
        <template #content>
            <CompassInformationBanner
                class="pdp-mandate-onboarding-company__information-banner"
                :title="$t.__('pdp.mandate.OnboardingCompany.InformationBanner.title')"
                sourceIllustration="/ressources/logged/img/illustrations/pdp/user-identity.svg"
            >
                <template #description>
                    <div
                        class="pdp-mandate-onboarding-company__information typographies-paragraph-medium-regular"
                        v-html="informationBannerDescription"
                    />
                </template>
            </CompassInformationBanner>
            <h2 class="pdp-mandate-onboarding-company__subtitle typographies-heading-h2">
                {{ $t.__('pdp.mandate.OnboardingCompany.subtitle') }}
            </h2>
            <div class="pdp-mandate-onboarding-company__form-item">
                <div class="pdp-mandate-onboarding-company__company-information">
                    <CompassDescriptionList size="small">
                        <CompassDescriptionListItem
                            v-for="(item, index) in formattedCompany"
                            :key="index"
                            :label="item.label"
                        >
                            {{ item.value }}
                        </CompassDescriptionListItem>
                    </CompassDescriptionList>
                </div>
            </div>
            <div class="pdp-mandate-onboarding-company__form-item">
                <CompassFormItem
                    :label="$t.__('pdp.mandate.OnboardingCompany.formItem.legal_representative')"
                    size="large"
                    :info="
                        $t.__('pdp.mandate.OnboardingCompany.formItem.legal_representative.info')
                    "
                >
                    <CompassSkeleton v-if="isLoading" size="x-large" />
                    <CompassSelect
                        v-else
                        v-model="selectedLegalRepresentative"
                        size="large"
                        menuClass="pdp-mandate-onboarding-company__company-legal-representative"
                    >
                        <CompassDropdownSelectOption
                            v-for="item in legalRepresentatives"
                            :key="item.name"
                            :label="item.name"
                            :value="item.name"
                        />
                    </CompassSelect>
                </CompassFormItem>
                <div
                    class="pdp-mandate-onboarding-company__help typographies-paragraph-small-semibold"
                >
                    <span>
                        {{
                            $t.__(
                                'pdp.mandate.OnboardingCompany.formItem.legal_representative.help',
                            )
                        }}
                    </span>
                    <CompassPlainButton
                        :label="$t.__('pdp.mandate.OnboardingCompany.support_button')"
                        size="small"
                        @click="openIntercom"
                    />
                </div>
            </div>
        </template>
        <template #footer>
            <FooterFullPage :primaryAction="primaryAction" :secondaryActions="secondaryActions" />
        </template>
    </PageLayoutForms>
</template>
<script>
import FooterFullPage from 'vcomponents/common/FooterFullPage/FooterFullPage.vue';
import PageLayoutForms from 'vcomponents/common/layout/pages/PageLayoutForms.vue';

export default {
    components: {
        FooterFullPage,
        PageLayoutForms,
    },
    props: {
        selectedMandateType: {
            type: Array,
            required: true,
            validator: (value) => value.includes('issue') || value.includes('receipt'),
        },
        company: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            primaryAction: {
                label: this.selectedMandateType.includes('receipt')
                    ? `${this.$t.__('common.actions.continue')} (1/2)`
                    : this.$t.__('common.actions.continue'),
                disabled: true,
                action: () => {
                    this.$emit('representativeSelected', this.selectedLegalRepresentativeData);
                },
            },
            secondaryActions: [
                {
                    label: this.$t.__('common.actions.cancel'),
                    action: () => {
                        this.$router.push({ name: 'dashboard' });
                    },
                },
            ],
            informationBannerDescription: this.$t.__(
                'pdp.mandate.OnboardingCompany.InformationBanner.description',
                {
                    paragraphStart: '<p>',
                    paragraphEnd: '</p>',
                },
            ),
            legalRepresentatives: [],
            selectedLegalRepresentative: '',
            isLoading: false,
        };
    },
    computed: {
        formattedCompany() {
            const company = {
                name: {
                    label: this.$t.__('common.labels.company_name'),
                    value: this.company.name,
                },
                type: {
                    label: this.$t.__('common.labels.company_type'),
                    value: this.company.legal_france.company_type || null,
                },
                capital: {
                    label: this.$t.__('common.labels.company_capital'),
                    value: this.company.capital || null,
                },
                vat: {
                    label: this.$t.__('common.labels.company_vat'),
                    value: this.company.legal_france.vat || null,
                },
                siret: {
                    label: this.$t.__('common.labels.siret'),
                    value: this.company.legal_france.siret || null,
                },
                siren: {
                    label: this.$t.__('common.labels.siren'),
                    value: this.company.legal_france.siren || null,
                },
                apeNafCode: {
                    label: this.$t.__('common.labels.ape_naf_code'),
                    value: this.company.legal_france.ape_naf_code || null,
                },
                rcsImmatriculation: {
                    label: this.$t.__('common.labels.rcs_immatriculation'),
                    value: this.company.legal_france.rcs_immatriculation || null,
                },
            };
            return Object.values(company).filter((item) => item.value);
        },
        selectedLegalRepresentativeData() {
            return this.legalRepresentatives.find(
                (person) => person.name === this.selectedLegalRepresentative,
            );
        },
    },
    watch: {
        selectedLegalRepresentative(newValue) {
            this.primaryAction.disabled = !newValue;
        },
    },
    created() {
        this.getLegalRepresentatives();
    },
    methods: {
        openIntercom() {
            if (window.Intercom) {
                window.Intercom('show');
            }
        },
        async getLegalRepresentatives() {
            this.isLoading = true;
            const controlId = await this.createControl();
            if (!controlId) {
                return [];
            }
            this.fetchRepresentatives(controlId)
                .then((representatives) => {
                    this.legalRepresentatives = representatives.reduce((accumulator, current) => {
                        if (
                            !accumulator.some(
                                (item) => item.name === `${current.firstName} ${current.lastName}`,
                            )
                        ) {
                            accumulator.push({
                                firstName: current.firstName,
                                lastName: current.lastName,
                                name: `${current.firstName} ${current.lastName}`,
                                birthDate: current.birthDate,
                            });
                        }
                        return accumulator;
                    }, []);
                })
                .catch((error) => {
                    this.$pushToast({
                        type: 'error',
                        text: error || this.$t.__('pdp.mandate.OnboardingCompany.fetch_error'),
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        fetchRepresentatives(controlId) {
            const controller = new AbortController();
            return new Promise((resolve) => {
                const interval = setInterval(() => {
                    axios
                        .get(`/kyc/${controlId}/representatives`, {
                            signal: controller.signal,
                        })
                        .then((response) => {
                            if (response.status === 200) {
                                controller.abort();
                                clearInterval(interval);
                                resolve(Object.values(response.data));
                            }
                        });
                }, 200);
            });
        },
        createControl() {
            return new Promise((resolve, reject) => {
                SellsyJS.WebServiceCaller.get(
                    '/kyc/create-control',
                    {
                        siren: this.company.legal_france.siren,
                    },
                    (result) => {
                        resolve(result.controlId);
                    },
                    () => {
                        reject(this.$t.__('pdp.mandate.OnboardingCompany.fetch_error'));
                    },
                );
            });
        },
    },
};
</script>
<style lang="scss">
body:has(.pdp-mandate-onboarding-company) {
    #sellsy_app {
        background-color: var(--colors-common-background-default);
    }
}
#compass-dropdown-container {
    .pdp-mandate-onboarding-company__company-search {
        width: auto;
    }
    .pdp-mandate-onboarding-company__company-legal-representative {
        z-index: 101;
    }
}
</style>
<style lang="scss" scoped>
.pdp-mandate-onboarding-company {
    &__information-banner {
        margin-bottom: var(--spacing-4);
    }

    &__information {
        color: var(--colors-common-text-brand-secondary-blue);

        &::v-deep {
            p {
                margin: 0;

                & + ul {
                    list-style: revert;
                    margin: var(--spacing-3) 0 0;
                    padding: revert;
                }
            }
        }
    }

    &__subtitle {
        margin: var(--spacing-5) 0 var(--spacing-4);
    }

    &__form-item {
        margin: 0 0 var(--spacing-4);

        &::v-deep {
            .compass-skeleton__line {
                height: var(--spacing-6);
            }
        }
    }

    &__help {
        color: var(--colors-common-text-subtle-default);
        margin: var(--spacing-1) 0 0;
    }

    &__company-information {
        background: var(--colors-common-background-subtle);
        border-radius: var(--spacing-1);
        color: var(--colors-common-text-standard-default);
        margin: var(--spacing-2) 0 var(--spacing-1);
        padding: var(--spacing-2);

        &-help {
            display: flex;
            gap: var(--spacing-1);
        }
    }
}
</style>
